import { c as _c } from "react/compiler-runtime";
import { Center, Spinner } from '@chakra-ui/react';
import { AdvisorChatAside } from '@src/components/sco/Advisors/layout/AdvisorChatAside';
import { AdvisorsMainPanel } from '@src/components/sco/Advisors/layout/AdvisorsMainPanel';
import { AdvisorsNav } from '@src/components/sco/Advisors/layout/AdvisorsNav';
import { AdvisorsToolbarExtrasProvider } from '@src/components/sco/Advisors/layout/AdvisorsToolbarExtras';
import { advisorsSubpath } from '@src/components/sco/Advisors/paths';
import { AdvisorsLandingSection } from '@src/components/sco/Advisors/sections/AdvisorsLandingSection';
import { AdvisorsLandingV2Section } from '@src/components/sco/Advisors/sections/AdvisorsLandingV2Section';
import { advisorBookSessionPath } from '@src/components/sco/Advisors/utils/sessionFeeWaiveCode';
import { AppLayout, attachedAppLayoutPanelProps } from '@src/components/sco/AppLayout';
import { ScoSiteNavBar } from '@src/components/sco/nav/ScoSiteNavBar';
import { HubspotHideChatStyles } from '@src/components/sco/utilities/HubspotChatHandler/HubspotHideChatStyles';
import { SSRGate } from '@src/components/sco/utilities/SSRGate';
import { BreadcrumbItem } from '@src/contexts/ActiveContentContext';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { useRouter } from 'next/router';
import { ReactNode, useEffect } from 'react';
type AdvisorsLayoutProps = {
  breadcrumbs?: BreadcrumbItem[];
  children: ReactNode;
  showAside?: boolean;
  /**
   * Nest inside parent chrome (e.g. Request Workspace) without site header.
   * Fills the parent panel instead of using 100vh.
   */
  embedded?: boolean;
  /** Hide hub nav (live session room). */
  focusMode?: boolean;
};

/**
 * Marketing landing in a single-panel AppLayout.
 * Used for guests on /advisors and always on /advisors/landing (pre-V2 copy).
 * Pass `version="v2"` for the current V2 LP at /advisors/v2.
 */
export const AdvisorsLandingLayout = t0 => {
  const $ = _c(11);
  const {
    version: t1
  } = t0;
  const version = t1 === undefined ? "v1" : t1;
  const isV2 = version === "v2";
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <HubspotHideChatStyles />;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ScoSiteNavBar />;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t4 = isV2 ? "advisors-landing-v2" : "advisors-landing";
  const t5 = isV2 ? "advisors-landing-v2-main" : "advisors-landing-main";
  let t6;
  if ($[2] !== isV2) {
    t6 = isV2 ? <AdvisorsLandingV2Section /> : <AdvisorsLandingSection />;
    $[2] = isV2;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  let t7;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      bgColor: "colorPalette.panel"
    };
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  let t8;
  if ($[5] !== t5 || $[6] !== t6) {
    t8 = [{
      id: t5,
      children: t6,
      containerProps: t7
    }];
    $[5] = t5;
    $[6] = t6;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] !== t4 || $[9] !== t8) {
    t9 = <SSRGate disableSSR={true}>{t2}<AppLayout header={t3} id={t4} panels={t8} /></SSRGate>;
    $[8] = t4;
    $[9] = t8;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  return t9;
};
export const AdvisorsLayout = t0 => {
  const $ = _c(52);
  const {
    breadcrumbs,
    children,
    showAside,
    embedded: t1,
    focusMode: t2
  } = t0;
  const embedded = t1 === undefined ? false : t1;
  const focusMode = t2 === undefined ? false : t2;
  const {
    currentUser,
    currentUserLoading
  } = useCurrentUser();
  const router = useRouter();
  const t3 = router.asPath || "";
  let t4;
  if ($[0] !== t3) {
    t4 = advisorsSubpath(t3);
    $[0] = t3;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const guestBookPath = t4 === "/sessions/new";
  const expertUserId = typeof router.query.expertUserId === "string" ? router.query.expertUserId : "";
  const waiveCode = typeof router.query.waive === "string" ? router.query.waive : "";
  const waitingForAuth = currentUserLoading && !currentUser;
  const isLoggedOut = !currentUser || !!currentUser.isGuest;
  let t5;
  let t6;
  if ($[2] !== expertUserId || $[3] !== guestBookPath || $[4] !== isLoggedOut || $[5] !== router || $[6] !== waiveCode) {
    t5 = () => {
      if (!isLoggedOut || !guestBookPath || !router.isReady || !expertUserId) {
        return;
      }
      router.replace(advisorBookSessionPath(expertUserId, waiveCode || null));
    };
    t6 = [expertUserId, guestBookPath, isLoggedOut, router, waiveCode];
    $[2] = expertUserId;
    $[3] = guestBookPath;
    $[4] = isLoggedOut;
    $[5] = router;
    $[6] = waiveCode;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  useEffect(t5, t6);
  if (waitingForAuth) {
    let t7;
    if ($[9] !== embedded) {
      t7 = !embedded && <HubspotHideChatStyles />;
      $[9] = embedded;
      $[10] = t7;
    } else {
      t7 = $[10];
    }
    let t8;
    if ($[11] !== embedded) {
      t8 = !embedded && <ScoSiteNavBar />;
      $[11] = embedded;
      $[12] = t8;
    } else {
      t8 = $[12];
    }
    const t9 = embedded ? "full" : undefined;
    const t10 = embedded ? "full" : "70vh";
    let t11;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <Spinner colorPalette="orange" size="lg" />;
      $[13] = t11;
    } else {
      t11 = $[13];
    }
    let t12;
    if ($[14] !== t10 || $[15] !== t9) {
      t12 = <Center bg="bg" height={t9} minHeight={t10}>{t11}</Center>;
      $[14] = t10;
      $[15] = t9;
      $[16] = t12;
    } else {
      t12 = $[16];
    }
    let t13;
    if ($[17] !== t12 || $[18] !== t7 || $[19] !== t8) {
      t13 = <SSRGate disableSSR={true}>{t7}{t8}{t12}</SSRGate>;
      $[17] = t12;
      $[18] = t7;
      $[19] = t8;
      $[20] = t13;
    } else {
      t13 = $[20];
    }
    return t13;
  }
  if (isLoggedOut) {
    if (embedded) {
      let t7;
      if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = <SSRGate disableSSR={true}><Center bg="bg" height="full" minHeight="full" padding={8}><Spinner colorPalette="orange" size="lg" /></Center></SSRGate>;
        $[21] = t7;
      } else {
        t7 = $[21];
      }
      return t7;
    }
    if (guestBookPath) {
      let t7;
      if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = <HubspotHideChatStyles />;
        $[22] = t7;
      } else {
        t7 = $[22];
      }
      let t8;
      if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
        t8 = <SSRGate disableSSR={true}>{t7}<Center bg="bg" minHeight="70vh"><Spinner colorPalette="orange" size="lg" /></Center></SSRGate>;
        $[23] = t8;
      } else {
        t8 = $[23];
      }
      return t8;
    }
    let t7;
    if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <AdvisorsLandingLayout version="v2" />;
      $[24] = t7;
    } else {
      t7 = $[24];
    }
    return t7;
  }
  let t7;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      display: "flex",
      flexDirection: "column",
      minHeight: 0,
      overflow: "hidden"
    };
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== breadcrumbs || $[27] !== children || $[28] !== focusMode || $[29] !== showAside) {
    t8 = focusMode ? children : <AdvisorsMainPanel breadcrumbs={breadcrumbs} showAside={showAside}>{children}</AdvisorsMainPanel>;
    $[26] = breadcrumbs;
    $[27] = children;
    $[28] = focusMode;
    $[29] = showAside;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  let t9;
  if ($[31] !== t8) {
    t9 = {
      id: "advisors-main",
      containerProps: t7,
      children: t8
    };
    $[31] = t8;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  const mainPanel = t9;
  let t10;
  if ($[33] !== focusMode || $[34] !== mainPanel || $[35] !== showAside) {
    t10 = focusMode ? [mainPanel] : [{
      id: "advisors-nav",
      children: <AdvisorsNav />,
      defaultWidth: 220,
      resizeHandlePosition: "right" as const,
      isResizable: true,
      useDrawerBreakpoints: {
        base: true,
        md: false
      },
      containerProps: {
        minHeight: 0,
        overflow: "hidden"
      }
    }, mainPanel, ...(showAside ? [{
      id: "advisors-aside",
      children: <AdvisorChatAside />,
      defaultWidth: 340,
      resizeHandlePosition: "left" as const,
      isResizable: true,
      useDrawerBreakpoints: {
        base: true,
        lg: false
      }
    }] : [])];
    $[33] = focusMode;
    $[34] = mainPanel;
    $[35] = showAside;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  const panels = t10;
  let t11;
  if ($[37] !== embedded) {
    t11 = !embedded && <HubspotHideChatStyles />;
    $[37] = embedded;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  let t12;
  if ($[39] !== embedded) {
    t12 = embedded ? null : <ScoSiteNavBar />;
    $[39] = embedded;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  const t13 = embedded ? "advisors-embedded" : "advisors";
  const t14 = embedded ? attachedAppLayoutPanelProps : undefined;
  let t15;
  if ($[41] !== embedded) {
    t15 = embedded ? {
      height: "full",
      maxHeight: "100%",
      minHeight: 0
    } : undefined;
    $[41] = embedded;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== panels || $[44] !== t12 || $[45] !== t13 || $[46] !== t14 || $[47] !== t15) {
    t16 = <AdvisorsToolbarExtrasProvider><AppLayout header={t12} id={t13} panelProps={t14} panels={panels} containerProps={t15} /></AdvisorsToolbarExtrasProvider>;
    $[43] = panels;
    $[44] = t12;
    $[45] = t13;
    $[46] = t14;
    $[47] = t15;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  let t17;
  if ($[49] !== t11 || $[50] !== t16) {
    t17 = <SSRGate disableSSR={true}>{t11}{t16}</SSRGate>;
    $[49] = t11;
    $[50] = t16;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  return t17;
};